import { Accordion, Box, Button, ButtonGroup } from "@chakra-ui/react";
import { Header } from "./components/header";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";

const faqData = [
  {
    question: "What is the Drug Store?",
    answer:
      "The Drug Store Assistant is an AI-powered tool that allows you to ask questions and query real-time, anonymized pharmacy data to uncover specific insights about medication trends, stock levels, and more.",
  },
  {
    question: "Does the platform collect patient data?",
    answer:
      "No. The platform is designed with privacy as a priority. It does not collect any patient-level data. All intelligence is derived from anonymized and aggregated data from participating pharmacies.",
  },
  {
    question: "How are disease trends identified?",
    answer:
      "Trends are identified by analyzing real-time, anonymously combined data from pharmacies across Nigeria using advanced AI models. This allows for monitoring trends at a national scale without compromising privacy.",
  },
  {
    question: "Who can access the dashboard?",
    answer:
      "Access to the dashboard is restricted to authorized users. You must have an account and be logged in to view the dashboard, analytics, and reports.",
  },
  {
    question: "Can reports be exported?",
    answer:
      "Yes, the platform allows you to generate reports based on the data to monitor trends and make informed decisions. This functionality is a core part of the dashboard.",
  },
  {
    question: "Can the platform detect unusual dispensing patterns?",
    answer:
      "Yes. A key feature of the platform is its ability to detect unusual patterns early, which helps support regulatory oversight and ensure compliance with data-backed evidence.",
  },
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const handleGetStartedClick = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      navigate("/register");
    }
  };

  return (
    <div className="">
      <div className="max-w-7xl! mx-auto! px-4! sm:px-6! lg:px-8!">
        <Header />

        <section
          id="hero"
          className="container! mx-auto! py-12! sm:py-16! lg:py-20! text-center! px-4!">
          <div className="max-w-4xl! mx-auto!">
            <h1 className="mb-6! sm:mb-8! font-semibold! text-3xl! sm:text-4xl! md:text-5xl! lg:text-6xl! leading-tight!">
              We turn pharmacy data into clear, actionable insight.
            </h1>

            <ButtonGroup
              spaceX="4"
              flexWrap="wrap"
              justifyContent="center"
              className="flex! flex-col! sm:flex-row! gap-3! sm:gap-0! items-center! justify-center!">
              <Button
                onClick={handleGetStartedClick}
                variant="solid"
                color="white"
                bg="blue.700"
                _hover={{ bg: "blue.800" }}
                className="w-full! sm:w-auto!">
                {isAuthenticated ? "Go to Dashboard" : "Get Started"}
              </Button>

              <Button
                variant="outline"
                color="blue.700"
                borderColor="blue.700"
                className="w-full! sm:w-auto!">
                Request Demo
              </Button>
            </ButtonGroup>
          </div>

          {/* Cards - Mobile */}
          <div className="mt-12! sm:mt-16! grid! grid-cols-1! gap-6! lg:hidden!">
            {["card-1.svg", "card-2.svg", "card-3.svg"].map((image, index) => (
              <div key={index} className="w-full! flex! justify-center!">
                <img
                  src={`assets/images/${image}`}
                  alt=""
                  className="w-full! max-w-md! object-contain!"
                />
              </div>
            ))}
          </div>

          {/* Cards - Desktop */}
          <div className="hidden! lg:flex! mt-16! justify-center!">
            <img
              src="assets/images/cards.svg"
              alt=""
              className="w-full! max-w-5xl! object-contain!"
            />
          </div>

          {/* Info Banner */}
          <div className="flex! flex-col! sm:flex-row! items-center! justify-center! gap-4! mt-10! sm:mt-12! mx-auto! text-center! sm:text-left!">
            <div className="w-10! h-10! flex! items-center! justify-center! rounded-md! bg-[#E6ECF7]! shrink-0!">
              <img
                src="assets/icons/ai-voice.svg"
                alt="AI Icon"
                className="w-5! h-5!"
              />
            </div>

            <p className="font-medium! text-sm! sm:text-base! text-[#302E2E]! max-w-2xl!">
              All insights are based on real-time data anonymously combined from
              pharmacies in Nigeria and analyzed using AI models.
            </p>
          </div>
        </section>

        {/* Why it Matters Section */}
        <section
          id="about"
          className="container! mx-auto! py-12! sm:py-16! lg:py-20! scroll-mt-20!">
          <div className="grid! grid-cols-1! lg:grid-cols-2! gap-8! lg:gap-12! items-center!">
            <div>
              <h3 className="font-semibold! text-2xl! sm:text-3xl! mb-6! sm:mb-8!">
                Why it matters
              </h3>

              <div className="grid! grid-cols-1! sm:grid-cols-2! gap-6! sm:gap-4!">
                <div className="space-y-3!">
                  <img
                    src="assets/icons/ai-analysis.svg"
                    alt=""
                    className="w-12! h-12!"
                  />
                  <h3 className="font-semibold! text-lg! sm:text-xl! text-[#302E2E]!">
                    Clarity at national scale
                  </h3>
                  <p className="text-sm! sm:text-base! text-[#605F5F]! font-medium!">
                    Monitor trends in pharmacies across Nigeria without manual
                    reporting or fragmented systems.
                  </p>
                </div>

                <div className="space-y-3!">
                  <img
                    src="assets/icons/ai-analysis.svg"
                    alt=""
                    className="w-12! h-12!"
                  />
                  <h3 className="font-semibold! text-lg! sm:text-xl! text-[#302E2E]!">
                    Better supply planning
                  </h3>
                  <p className="text-sm! sm:text-base! text-[#605F5F]! font-medium!">
                    Align stock availability with real-world demand to reduce
                    shortages and waste.
                  </p>
                </div>

                <div className="space-y-3!">
                  <img
                    src="assets/icons/ai-analysis.svg"
                    alt=""
                    className="w-12! h-12!"
                  />
                  <h3 className="font-semibold! text-lg! sm:text-xl! text-[#302E2E]!">
                    Support regulatory oversight
                  </h3>
                  <p className="text-sm! sm:text-base! text-[#605F5F]! font-medium!">
                    Detect unusual patterns early and support compliance with
                    data-backed evidence.
                  </p>
                </div>

                <div className="space-y-3!">
                  <img
                    src="assets/icons/ai-analysis.svg"
                    alt=""
                    className="w-12! h-12!"
                  />
                  <h3 className="font-semibold! text-lg! sm:text-xl! text-[#302E2E]!">
                    Privacy by design
                  </h3>
                  <p className="text-sm! sm:text-base! text-[#605F5F]! font-medium!">
                    No patient-level data. All intelligence is anonymized,
                    aggregated, and secure.
                  </p>
                </div>
              </div>

              <ButtonGroup
                spaceX="4"
                flexWrap="wrap"
                justifyContent="start"
                className="flex! flex-col! sm:flex-row! gap-3! sm:gap-0! mt-8!">
                <Button
                  onClick={handleGetStartedClick}
                  variant="solid"
                  color="white"
                  bg="blue.700"
                  _hover={{ bg: "blue.800" }}
                  className="w-full! sm:w-auto!">
                  {isAuthenticated ? "Go to Dashboard" : "Get Started"}
                </Button>

                <Button
                  variant="outline"
                  color="blue.700"
                  borderColor="blue.700"
                  className="w-full! sm:w-auto!">
                  Request Demo
                </Button>
              </ButtonGroup>
            </div>

            <div className="w-full! order-first! lg:order-last!">
              <img src="assets/images/card-1.svg" className="w-full!" alt="" />
            </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section
          id="how-it-works"
          className="bg-[#f7faff]! py-12! sm:py-16! lg:py-20! -mx-4! sm:-mx-6! lg:-mx-8! px-4! sm:px-6! lg:px-8! scroll-mt-20!">
          <div className="max-w-7xl! mx-auto!">
            <h3 className="text-center! font-semibold! text-2xl! sm:text-3xl! mb-8! sm:mb-12!">
              How it works
            </h3>

            <div className="max-w-5xl! mx-auto! grid! grid-cols-1! sm:grid-cols-2! gap-4! sm:gap-6!">
              <div className="bg-[#E6ECF7]! p-5! sm:p-6! rounded-xl! relative! overflow-hidden! min-h-[160px]! sm:min-h-[180px]!">
                <h4 className="text-base! sm:text-lg! font-semibold! text-[#302E2E]! mb-2!">
                  Get Started
                </h4>
                <p className="text-sm! sm:text-base! font-medium! text-[#605F5F]! max-w-[60%]! sm:max-w-[65%]!">
                  Log in securely to access your dashboard.
                </p>

                <img
                  src="assets/icons/icon-1.svg"
                  className="w-20! sm:w-24! lg:w-28! absolute! bottom-2! right-2!"
                  alt=""
                />
              </div>

              <div className="bg-[#E6ECF7]! p-5! sm:p-6! rounded-xl! relative! overflow-hidden! min-h-[160px]! sm:min-h-[180px]!">
                <h4 className="text-base! sm:text-lg! font-semibold! text-[#302E2E]! mb-2!">
                  Explore or ask
                </h4>
                <p className="text-sm! sm:text-base! font-medium! text-[#605F5F]! max-w-[55%]! sm:max-w-[60%]!">
                  Filter the data or ask the Drug Store Assistant questions to
                  uncover specific insights.
                </p>

                <img
                  src="assets/icons/icon-2.svg"
                  className="w-32! sm:w-36! lg:w-40! absolute! bottom-2! right-2!"
                  alt=""
                />
              </div>

              <div className="bg-[#E6ECF7]! p-5! sm:p-6! rounded-xl! relative! overflow-hidden! min-h-[160px]! sm:min-h-[180px]!">
                <h4 className="text-base! sm:text-lg! font-semibold! text-[#302E2E]! mb-2!">
                  Access Data
                </h4>
                <p className="text-sm! sm:text-base! font-medium! text-[#605F5F]! max-w-[60%]! sm:max-w-[65%]!">
                  Explore key public health, inventory, and compliance metrics
                  in one place.
                </p>

                <img
                  src="assets/icons/icon-3.svg"
                  className="w-28! sm:w-32! lg:w-36! absolute! bottom-2! right-2!"
                  alt=""
                />
              </div>

              <div className="bg-[#E6ECF7]! p-5! sm:p-6! rounded-xl! relative! overflow-hidden! min-h-[160px]! sm:min-h-[180px]!">
                <h4 className="text-base! sm:text-lg! font-semibold! text-[#302E2E]! mb-2!">
                  Generate Reports
                </h4>
                <p className="text-sm! sm:text-base! font-medium! text-[#605F5F]! max-w-[60%]! sm:max-w-[65%]!">
                  Generate reports, monitor trends, and make informed decisions.
                </p>

                <img
                  src="assets/icons/icon-4.svg"
                  className="w-28! sm:w-32! lg:w-36! absolute! bottom-2! right-2!"
                  alt=""
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="faq"
          className="container! mx-auto! py-12! sm:py-16! lg:py-20! px-4! scroll-mt-20! grid md:grid-cols-2! gap-8! grid-cols-1">
          <h3 className="font-semibold! text-2xl! sm:text-3xl! mb-6! sm:mb-8! text-center! lg:text-left!">
            Frequently asked questions
          </h3>

          <div className="max-w-4xl! mx-auto! lg:mx-0!">
            <Accordion.Root collapsible>
              {faqData.map((faq, index) => (
                <Accordion.Item
                  key={index}
                  value={`item-${index}`}
                  className="mb-4!">
                  <Accordion.ItemTrigger
                    bg="blue.50"
                    color="#302E2E"
                    border={0}
                    _hover={{ bg: "blue.100" }}
                    borderRadius="md"
                    px={4}
                    py={4}
                    className="text-left!">
                    <Box
                      flex="1"
                      textAlign="left"
                      fontWeight="medium"
                      className="text-sm! sm:text-base!">
                      {faq.question}
                    </Box>

                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>

                  <Accordion.ItemContent>
                    <Accordion.ItemBody
                      pt={4}
                      pb={4}
                      color="#605F5F"
                      fontWeight="medium"
                      className="text-sm! sm:text-base!">
                      {faq.answer}
                    </Accordion.ItemBody>
                  </Accordion.ItemContent>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container! mx-auto! py-12! sm:py-16! lg:py-20! px-4!">
          <div className="bg-[#0142B1]! p-6! sm:p-8! lg:p-12! rounded-2xl! sm:rounded-3xl! max-w-5xl! mx-auto! flex! flex-col! lg:flex-row! items-center! justify-between! gap-6! lg:gap-8!">
            <div className="text-center! lg:text-left!">
              <h1 className="text-2xl! sm:text-3xl! lg:text-4xl! max-w-xl! font-semibold! text-white! mb-4! sm:mb-6!">
                Make informed decisions with real-time pharmacy intelligence.
              </h1>

              <Button
                variant="solid"
                bg="white"
                color="blue.800"
                _hover={{ bg: "gray.100" }}
                onClick={handleGetStartedClick}
                className="w-full! sm:w-auto!">
                {isAuthenticated ? "Go to Dashboard" : "Get started"}
              </Button>
            </div>

            <img
              src="assets/icons/icon-x.svg"
              className="w-32! sm:w-40! lg:w-auto! shrink-0!"
              alt=""
            />
          </div>
        </section>
      </div>
      <footer
        className="bg-[#0F172A]! text-white! py-12! sm:py-16!   px-4! sm:px-6! lg:px-8!"
        id="contact">
        <div className="max-w-7xl! mx-auto!">
          <div className="grid! grid-cols-1! sm:grid-cols-2! lg:grid-cols-4! gap-8! sm:gap-10! lg:gap-12!">
            {/* Brand Column */}
            <div className="sm:col-span-2! lg:col-span-1!">
              <img
                src="assets/logo/logo.svg"
                className="w-24! sm:w-28! mb-4!"
                alt="Drug Store Logo"
              />
              <p className="text-sm! sm:text-base! text-gray-300! max-w-xs!">
                Real-time pharmacy intelligence powered by AI and anonymized
                national data.
              </p>
            </div>

            {/* Platform Links */}
            <div>
              <h4 className="font-semibold! text-base! sm:text-lg! mb-3! sm:mb-4!">
                Platform
              </h4>
              <ul className="space-y-2! sm:space-y-3! text-sm! sm:text-base! text-gray-300!">
                <li>
                  <a
                    href="#about"
                    className="hover:text-white! transition-colors!">
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#how-it-works"
                    className="hover:text-white! transition-colors!">
                    How it works
                  </a>
                </li>
                <li>
                  <a
                    href="#faq"
                    className="hover:text-white! transition-colors!">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold! text-base! sm:text-lg! mb-3! sm:mb-4!">
                Legal
              </h4>
              <ul className="space-y-2! sm:space-y-3! text-sm! sm:text-base! text-gray-300!">
                <li>
                  <a href="#" className="hover:text-white! transition-colors!">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white! transition-colors!">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold! text-base! sm:text-lg! mb-3! sm:mb-4!">
                Contact
              </h4>
              <p className="text-sm! sm:text-base! text-gray-300! mb-2!">
                <a
                  href="mailto:analytics@drugstore.ng"
                  className="hover:text-white! transition-colors!">
                  analytics@drugstore.ng
                </a>
              </p>
            </div>
          </div>

          <div className="border-t! border-gray-700! mt-10! sm:mt-12! pt-6! sm:pt-8! text-center! text-xs! sm:text-sm! text-gray-400!">
            © {new Date().getFullYear()} Drug Store. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};
